import { AbstractFreewheelClass } from 'tag/ts/tag/ads/freewheel/abstract/AbstractFreewheelClass';
import { FreewheelParameterType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelParameterType';
import { FreewheelRequestType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelRequestType';
import { IFreewheelParameter } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelParameter';
import { IFreewheelRequest } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequest';
import { IFreewheelRequestArgument } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequestArgument';
import { FmsParameter } from 'tag/ts/tag/ads/freewheel/parameters/FmsParameter';
import { IParamsFMS } from 'tag/ts/tag/interfaces/params/IParamsFMS';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Base class for all Freewheel requests
 */
export abstract class AbstractFreewheelRequest extends AbstractFreewheelClass implements IFreewheelRequest {

	/**
	 * @description Type of request
	 */
	type: FreewheelRequestType;
	/**
	 * @description Potential keys in FMS response which should NOT be passed to Freewheel ad call from Desktop.
	 */
	readonly #excludedFMSKeys = [
		'_fw_app_bundle',
		'_fw_atts',
		'_fw_did',
		'_fw_is_lat',
		'_fw_nielsen_app_id',
		'fms_idfv',
		'fms_ifa',
		'imafw__fw_app_bundle',
		'imafw__fw_atts',
		'imafw__fw_did',
		'imafw__fw_is_lat',

		// should be retrieved from siteSessionData.region
		'region',
		'imafw_region'
	];
	/**
	 * @description Parameters in request
	 */
	#parameters: IFreewheelParameter[];
	/**
	 * @description Prefix required by all parameter keys when making a DAI ad call.
	 */
	readonly #daiPrefix = 'imafw_';

	protected constructor( argument: IFreewheelRequestArgument ) {
		super( {
			...argument,
			name: 'AbstractFreewheelRequest'
		} );

		this.type = argument.type;

		// init params
		this.#parameters = [];
		argument.parameters?.forEach( ( p ) => this.addParameter( p ) );
	}

	get parameters(): IFreewheelParameter[] {
		return this.#parameters;
	}

	/**
	 * Returns boolean indicating whether this request uses DAI or not
	 * @returns {boolean}
	 */
	isDaiRequest(): boolean {
		return [
			FreewheelRequestType.DAI_API,
			FreewheelRequestType.DAI_SDK,
			FreewheelRequestType.DAI_SSB
		].includes( this.type );
	}

	/**
	 * @description Returns parameter from request, if found
	 * @param {string} key
	 * @return {IFreewheelParameter | undefined}
	 */
	getParameter( key: string ): IFreewheelParameter | undefined {
		if ( !( this.utils.Type.isString( key ) && key.length > 0 ) ) {
			this?.logger?.warn( 'getParameter', 'invalid key:', key );
			return;
		}

		const foundParameter = this.#parameters.find( ( p ) => p.key === key ) || undefined;

		if ( this.utils.Type.isEmpty( foundParameter ) ) {
			this?.logger?.log( 'getParameter', 'parameter not found', 'key:', key );
			return;
		}

		return foundParameter;
	}

	/**
	 * @description Returns parameters which match the type passed
	 * @param {FreewheelParameterType[]} types
	 * @return {IFreewheelParameter[]}
	 */
	getParametersByType( types: FreewheelParameterType[] ): IFreewheelParameter[] {
		return this.#parameters.filter( ( param ) => types.includes( param.type ) );
	}

	/**
	 * @description Returns global parameters
	 * @return {IFreewheelParameter[]}
	 */
	getGlobalParameters(): IFreewheelParameter[] {
		return this.#parameters.filter( ( param ) => param.type === FreewheelParameterType.GLOBAL );
	}

	/**
	 * @description Returns site parameters
	 * @return {IFreewheelParameter[]}
	 */
	getSiteParameters(): IFreewheelParameter[] {
		return this.#parameters.filter( ( param ) => param.type === FreewheelParameterType.SITE );
	}

	/**
	 * @description Returns custom parameters
	 * @return {IFreewheelParameter[]}
	 */
	getCustomParameters(): IFreewheelParameter[] {
		return this.#parameters.filter( ( param ) => param.type === FreewheelParameterType.CUSTOM );
	}

	/**
	 * @description Adds FMS parameters to request. Adds DAI prefix to keys, if necessary
	 * @param {IParamsFMS} fms
	 * @returns {IFreewheelParameter[]}
	 */
	addFmsParameters( fms: IParamsFMS ): void {
		this?.logger?.log( 'addFmsParameters', 'creating parameters from FMS response...', '\nfms:', fms, '\nkeys to filter out:', this.#excludedFMSKeys );
		if ( this.utils.Type.isEmpty( fms ) || !this.utils.Type.isObject( fms ) ) {
			this?.logger?.warn( 'addFmsParameters', 'missing/invalid FMS object:', fms );
			return;
		}

		Object.keys( fms )
			.filter( ( key ) => {
				if ( this.#excludedFMSKeys.includes( key ) ) {
					this?.logger?.log( 'addFmsParameters', 'filtering out excluded FMS parameter...', '\nkey:', key, '\nvalue:', fms[ key ] );
					return false;
				}
				if ( this.utils.Type.isNullOrUndefined( fms[ key ] ) ) {
					this?.logger?.log( 'addFmsParameters', 'filtering out null/undefined FMS parameter...', '\nkey:', key, '\nvalue:', fms[ key ] );
					return false;
				}
				return true;
			} )
			.map( ( key ) => {
				// if request is DAI, then ensure the key has the DAI prefix
				if ( this.isDaiRequest() && !key.startsWith( this.#daiPrefix ) ) {
					return new FmsParameter( `${this.#daiPrefix}${key}`, fms[ key ] );
				}
				return new FmsParameter( key, fms[ key ] );
			} )
			.forEach( ( fmsParam: FmsParameter ) => {
				const preexistingParam = this.getParameter( fmsParam.key );
				if ( !this.utils.Type.isEmpty( preexistingParam ) ) {
					this?.logger?.warn(
						'addFmsParameters',
						'adding FMS parameter with duplicate key as a pre-existing parameter...',
						'\nduplicate key: ',
						fmsParam.key,
						'\nFMS param: ',
						fmsParam,
						'\npre-existing param: ',
						preexistingParam
					);
				}
				this.addParameter( fmsParam );
			} );
	}

	/**
	 * @description Appends parameter to request
	 * @param {IFreewheelParameter} parameter
	 */
	protected addParameter( parameter: IFreewheelParameter ): void {
		if ( this.utils.Type.isNullOrUndefined( parameter?.type ) ) {
			this?.logger?.error( 'addParameter', 'parameter is missing type, not adding:', parameter );
			return;
		}
		if ( this.utils.Type.isNullOrUndefined( parameter?.key ) ) {
			this?.logger?.error( 'addParameter', 'parameter is missing key, not adding:', parameter );
			return;
		}
		if ( this.utils.Type.isNullOrUndefined( parameter?.value ) ) {
			this?.logger?.log( 'addParameter', 'parameter is missing value (could be an error or intended to be omitted), not adding:', parameter );
			return;
		}

		this.#parameters.push( parameter );
		this?.logger?.log( 'addParameter', 'added:', parameter, 'parameters:', this.#parameters );
	}

	/**
	 * @description Removes parameter from request, if set
	 * @param {string} key
	 */
	protected removeParameter( key: string ): IFreewheelParameter | undefined {
		if ( !( this.utils.Type.isString( key ) && key.length > 0 ) ) {
			this?.logger?.warn( 'removeParameter', 'invalid key:', key );
			return;
		}

		const foundParameter = this.getParameter( key );

		if ( this.utils.Type.isEmpty( foundParameter ) ) {
			this?.logger?.log( 'removeParameter', 'parameter not found', 'key:', key );
			return;
		}

		this.#parameters.splice( this.#parameters.indexOf( foundParameter ), 1 );
		this?.logger?.log( 'removeParameter', 'removed:', foundParameter, '\nparameters:', this.#parameters );

		return foundParameter;
	}
}